import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MetadataDefinitionApi } from '../../services/ApiService';

function EndeavorMetadataDefinitionList() {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMetadataDefinitions();
  }, []);

  const loadMetadataDefinitions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await MetadataDefinitionApi.getEndeavorMetadataDefinitions();
      if (!response.ok) {
        throw new Error('Failed to load metadata definitions');
      }
      const data = await response.json();
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading metadata definitions:', error);
      setError('Failed to load metadata definitions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, fieldName) => {
    if (!window.confirm(`Are you sure you want to delete the metadata definition "${fieldName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Note: You may need to implement a delete endpoint
      console.log('Delete functionality not implemented yet for metadata definition:', id);
      // const response = await MetadataDefinitionApi.deleteEndeavorMetadataDefinition(id);
      // if (response.ok) {
      //   loadMetadataDefinitions();
      // }
    } catch (error) {
      console.error('Error deleting metadata definition:', error);
      setError('Failed to delete metadata definition. Please try again.');
    }
  };

  const filteredDefinitions = definitions.filter(definition =>
    definition.fieldName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    definition.fieldType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    definition.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading endeavor metadata definitions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Endeavor Metadata Definitions</h2>
        <Link to="/settings/endeavor-metadata-definitions/create">
          <button type="button">Create New Definition</button>
        </Link>
      </div>
      
      {error && (
        <div className="error-message" style={{ 
          color: 'var(--color-brand-danger, #dc3545)', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid var(--intent-danger-border, #f5c6cb)', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <div className="search-container" style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search metadata definitions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ 
            width: '100%', 
            maxWidth: '400px',
            padding: '0.5rem',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </div>

      {filteredDefinitions.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: 'var(--text-muted, #666)',
          backgroundColor: 'var(--semantic-surface-panel, #f9f9f9)',
          borderRadius: '4px'
        }}>
          {searchTerm ? 'No metadata definitions match your search.' : 'No metadata definitions found. Create your first one!'}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Field Name</th>
              <th>Field Type</th>
              <th>Description</th>
              <th>Required</th>
              <th>Display Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDefinitions.map((definition) => {
              // FIXED: Handle different possible ID field names
              const defId = definition.metadataDefinitionId || definition.id;
              
              return (
                <tr key={defId}>
                  <td>
                    <strong>{definition.fieldName || 'Unnamed Field'}</strong>
                    {definition.defaultValue && (
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Default: {definition.defaultValue}
                      </div>
                    )}
                  </td>
                  <td>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'var(--badge-neutral-bg, #e9ecef)',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      {definition.fieldType || 'TEXT'}
                    </span>
                  </td>
                  <td>{definition.description || 'No description'}</td>
                  <td>
                    <span style={{
                      color: definition.isRequired ? '#28a745' : '#6c757d',
                      fontWeight: '500'
                    }}>
                      {definition.isRequired ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{definition.displayOrder || 1}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Link 
                        to={`/settings/endeavor-metadata-definitions/${defId}`}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'none',
                          fontSize: '0.875rem'
                        }}
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(defId, definition.fieldName)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#dc3545', 
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          padding: '0'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

          </tbody>
        </table>
      )}

      <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'var(--semantic-surface-panel, #f8f9fa)', borderRadius: '4px' }}>
        <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary, #495057)' }}>About Metadata Definitions</h4>
        <p style={{ margin: '0', fontSize: '0.875rem', color: 'var(--text-muted, #6c757d)' }}>
          Metadata definitions allow you to create custom fields for your endeavors. These fields help you categorize, 
          track, and organize your learning activities with additional information like subject areas, difficulty levels, 
          or any other attributes relevant to your learning goals.
        </p>
      </div>
    </div>
  );
}

export default EndeavorMetadataDefinitionList;

// Add these routes to your Settings.js Routes section:
// <Route path="endeavor-metadata-definitions" element={<ProtectedRoute><EndeavorMetadataDefinitionList /></ProtectedRoute>} />
// <Route path="endeavor-metadata-definitions/create" element={<ProtectedRoute><EndeavorMetadataDefinitionEdit /></ProtectedRoute>} />
// <Route path="endeavor-metadata-definitions/:id" element={<ProtectedRoute><EndeavorMetadataDefinitionEdit /></ProtectedRoute>} />
// <Route path="event-types" element={<ProtectedRoute><CreateEditEventType /></ProtectedRoute>} />
// <Route path="event-types/create" element={<ProtectedRoute><CreateEditEventType /></ProtectedRoute>} />
// <Route path="event-types/:id" element={<ProtectedRoute><CreateEditEventType /></ProtectedRoute>} />